import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import {
  submitChemistryAssessment,
  fetchBoardMembers,
} from "../../services/businessDashApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import {
  CheckCircle,
  Circle,
  ArrowRight,
  ArrowLeft,
  Save,
  Users,
  Target,
  TrendingUp,
  Brain,
  X,
} from "lucide-react";
import OrganicShape from "./OrganicShape";

const assessmentQuestions = [
  {
    id: 1,
    title: "Sustainability Investment Decision",
    scenario:
      "Your board needs to approve a €5M investment in renewable energy infrastructure with a 7-year payback period. How should the decision process unfold?",
    options: [
      {
        id: "a",
        text: "CEO presents recommendation, board votes immediately",
        value: 2,
        category: "decision-making",
      },
      {
        id: "b",
        text: "Committee analysis, board discussion, then vote",
        value: 4,
        category: "decision-making",
      },
      {
        id: "c",
        text: "Extended stakeholder consultation before any decision",
        value: 3,
        category: "collaboration",
      },
      {
        id: "d",
        text: "Data-driven analysis with clear sustainability metrics",
        value: 5,
        category: "sustainability",
      },
    ],
    defaultAnswer: "b",
  },
  {
    id: 2,
    title: "Board Meeting Dynamics",
    scenario:
      "During board meetings, how should dissenting opinions on ESG initiatives be handled?",
    options: [
      {
        id: "a",
        text: "Discourage dissent to maintain unity",
        value: 1,
        category: "communication",
      },
      {
        id: "b",
        text: "Allow brief discussion, then move on",
        value: 3,
        category: "communication",
      },
      {
        id: "c",
        text: "Encourage diverse perspectives and debate",
        value: 5,
        category: "collaboration",
      },
      {
        id: "d",
        text: "Reserve dissent for executive sessions only",
        value: 2,
        category: "decision-making",
      },
    ],
    defaultAnswer: "c",
  },
  {
    id: 3,
    title: "Crisis Communication",
    scenario:
      "A sustainability scandal emerges. How should the board communicate internally?",
    options: [
      {
        id: "a",
        text: "CEO briefs board via email updates",
        value: 2,
        category: "communication",
      },
      {
        id: "b",
        text: "Emergency board meeting with full transparency",
        value: 5,
        category: "communication",
      },
      {
        id: "c",
        text: "Committee chairs brief their respective members",
        value: 3,
        category: "collaboration",
      },
      {
        id: "d",
        text: "Wait for legal counsel before any communication",
        value: 1,
        category: "decision-making",
      },
    ],
    defaultAnswer: "b",
  },
  {
    id: 4,
    title: "Performance Metrics",
    scenario:
      "How should the board track sustainability performance between meetings?",
    options: [
      {
        id: "a",
        text: "Quarterly reports from management",
        value: 3,
        category: "communication",
      },
      {
        id: "b",
        text: "Real-time dashboard access for all directors",
        value: 5,
        category: "sustainability",
      },
      {
        id: "c",
        text: "Annual comprehensive review only",
        value: 1,
        category: "decision-making",
      },
      {
        id: "d",
        text: "Committee-based monitoring with summaries",
        value: 4,
        category: "collaboration",
      },
    ],
    defaultAnswer: "b",
  },
  {
    id: 5,
    title: "Strategic Planning",
    scenario:
      "When developing the company's 2030 sustainability strategy, what's the ideal board involvement?",
    options: [
      {
        id: "a",
        text: "Review and approve management's plan",
        value: 2,
        category: "decision-making",
      },
      {
        id: "b",
        text: "Co-create strategy with management team",
        value: 5,
        category: "collaboration",
      },
      {
        id: "c",
        text: "Provide high-level guidance only",
        value: 1,
        category: "communication",
      },
      {
        id: "d",
        text: "Set targets, let management determine approach",
        value: 4,
        category: "sustainability",
      },
    ],
    defaultAnswer: "b",
  },
  {
    id: 6,
    title: "Stakeholder Engagement",
    scenario:
      "How should the board engage with external sustainability stakeholders?",
    options: [
      {
        id: "a",
        text: "Through management representatives only",
        value: 1,
        category: "communication",
      },
      {
        id: "b",
        text: "Direct engagement at key meetings",
        value: 4,
        category: "collaboration",
      },
      {
        id: "c",
        text: "Regular structured stakeholder sessions",
        value: 5,
        category: "sustainability",
      },
      {
        id: "d",
        text: "Annual stakeholder survey review",
        value: 2,
        category: "decision-making",
      },
    ],
    defaultAnswer: "c",
  },
  {
    id: 7,
    title: "Board Composition",
    scenario:
      "Your board needs new sustainability expertise. What's the best approach?",
    options: [
      {
        id: "a",
        text: "Add one sustainability expert",
        value: 3,
        category: "collaboration",
      },
      {
        id: "b",
        text: "Upskill existing directors through training",
        value: 4,
        category: "communication",
      },
      {
        id: "c",
        text: "Create sustainability advisory panel",
        value: 2,
        category: "decision-making",
      },
      {
        id: "d",
        text: "Embed sustainability knowledge across all directors",
        value: 5,
        category: "sustainability",
      },
    ],
    defaultAnswer: "d",
  },
  {
    id: 8,
    title: "Decision Speed vs. Consensus",
    scenario:
      "A time-sensitive green technology partnership opportunity arises. How should the board respond?",
    options: [
      {
        id: "a",
        text: "Emergency vote within 48 hours",
        value: 2,
        category: "decision-making",
      },
      {
        id: "b",
        text: "Delegate authority to CEO with board ratification",
        value: 4,
        category: "collaboration",
      },
      {
        id: "c",
        text: "Fast-track committee review and recommendation",
        value: 5,
        category: "decision-making",
      },
      {
        id: "d",
        text: "Standard process regardless of timing",
        value: 1,
        category: "communication",
      },
    ],
    defaultAnswer: "c",
  },
];

export default function BoardChemistryAssessment({
  isOpen = false,
  onClose,
  onComplete,
  onSave,
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [savedProgress, setSavedProgress] = useState(false);
  const [realBoardMembers, setRealBoardMembers] = useState([]);

  useEffect(() => {
    fetchBoardMembers()
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : data?.results ?? data?.data ?? [];
        const active = list
          .filter(
            (m) => m.is_active !== false && m.approval_status !== "rejected"
          )
          .map((m) => ({
            name:
              m.name ||
              m.full_name ||
              `${m.first_name || ""} ${m.last_name || ""}`.trim(),
          }))
          .filter((m) => m.name);
        if (active.length > 0) setRealBoardMembers(active);
      })
      .catch(() => {});
  }, []);

  // Initialize with smart defaults
  useState(() => {
    const defaultAnswers = {};
    assessmentQuestions.forEach((question) => {
      if (question.defaultAnswer) {
        defaultAnswers[question.id] = question.defaultAnswer;
      }
    });
    setAnswers(defaultAnswers);
  });

  const progress = ((currentQuestion + 1) / assessmentQuestions.length) * 100;
  const isLastQuestion = currentQuestion === assessmentQuestions.length - 1;
  const canProceed = answers[assessmentQuestions[currentQuestion].id];

  const handleAnswer = (questionId, answerId) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answerId }));
  };

  const handleNext = () => {
    if (isLastQuestion) {
      calculateResults();
    } else {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentQuestion((prev) => Math.max(0, prev - 1));
  };

  const handleSave = () => {
    setSavedProgress(true);
    onSave?.({ currentQuestion, answers, timestamp: new Date().toISOString() });
    setTimeout(() => setSavedProgress(false), 2000);
  };

  const calculateResults = async () => {
    // Note: The backend extracts the user ID from the JWT token
    // No need to send board_member_id in the payload

    const categoryScores = {
      collaboration: 0,
      "decision-making": 0,
      communication: 0,
      sustainability: 0,
    };

    const categoryCounts = {
      collaboration: 0,
      "decision-making": 0,
      communication: 0,
      sustainability: 0,
    };

    // Build answers array for backend
    const answersArray = [];

    assessmentQuestions.forEach((question) => {
      const selectedAnswer = answers[question.id];
      if (selectedAnswer) {
        const option = question.options.find(
          (opt) => opt.id === selectedAnswer
        );
        if (option) {
          categoryScores[option.category] += option.value;
          categoryCounts[option.category]++;

          // Add to backend format
          answersArray.push({
            question_id: question.id,
            selected_option: selectedAnswer,
            score: option.value,
            category: option.category,
          });
        }
      }
    });

    const averageScores = Object.keys(categoryScores).reduce(
      (acc, category) => {
        acc[category] =
          categoryCounts[category] > 0
            ? categoryScores[category] / categoryCounts[category]
            : 0;
        return acc;
      },
      {}
    );

    // Submit to backend API
    // The backend will extract the user ID from the JWT token
    console.log("🔍 Debug - Answers Array Length:", answersArray.length);
    console.log("🔍 Debug - Answers Array:", answersArray);

    try {
      if (answersArray.length === 8) {
        const totalScore = Object.values(categoryScores).reduce(
          (a, b) => a + b,
          0
        );

        const payload = {
          // board_member_id is NOT sent - backend extracts from JWT token
          answers: answersArray,
          total_score: totalScore,
          decision_making_score: categoryScores["decision-making"] || 0,
          collaboration_score: categoryScores.collaboration || 0,
          sustainability_score: categoryScores.sustainability || 0,
          communication_score: categoryScores.communication || 0,
        };

        console.log("📤 Submitting assessment to API:", payload);
        await submitChemistryAssessment(payload);
        console.log(
          "✅ Chemistry assessment submitted to backend successfully!"
        );
      } else {
        console.error("❌ Assessment not submitted - Missing requirements:");
        console.error("Answers Length:", answersArray.length);
        console.error("Expected 8 answers");
      }
    } catch (error) {
      console.error("Failed to submit assessment to backend:", error);
      alert(
        "Assessment saved locally but failed to sync with server. Please try again later."
      );
    }

    const overallScore =
      Object.values(averageScores).reduce((sum, score) => sum + score, 0) / 4;

    const membersForResults =
      realBoardMembers.length > 0
        ? realBoardMembers.map((m) => ({
            name: m.name,
            collaboration: averageScores.collaboration ?? 0,
            decisionMaking: averageScores["decision-making"] ?? 0,
            communication: averageScores.communication ?? 0,
            sustainability: averageScores.sustainability ?? 0,
            overallScore: parseFloat(overallScore.toFixed(2)),
          }))
        : [];

    const results = {
      scores: averageScores,
      overallScore,
      completedAt: new Date().toISOString(),
      recommendations: generateRecommendations(averageScores),
      boardMembers: membersForResults,
    };

    setShowResults(true);
    onComplete?.(results);
    return results;
  };

  const generateRecommendations = (scores) => {
    const recommendations = [];

    if (scores.collaboration < 3.5) {
      recommendations.push({
        area: "Board Collaboration",
        priority: "High",
        suggestion:
          "Implement structured brainstorming sessions and cross-committee collaboration initiatives",
      });
    }

    if (scores["decision-making"] < 3.5) {
      recommendations.push({
        area: "Decision-Making Process",
        priority: "High",
        suggestion:
          "Establish clear decision frameworks with defined approval thresholds and escalation paths",
      });
    }

    if (scores.communication < 3.5) {
      recommendations.push({
        area: "Board Communication",
        priority: "Medium",
        suggestion:
          "Implement regular communication protocols and feedback mechanisms between meetings",
      });
    }

    if (scores.sustainability < 4.0) {
      recommendations.push({
        area: "Sustainability Integration",
        priority: "High",
        suggestion:
          "Enhance sustainability literacy and embed ESG considerations into all board decisions",
      });
    }

    return recommendations;
  };

  if (showResults) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              Assessment Complete!
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
              <p className="text-foreground/80">
                Your board chemistry analysis is ready.
              </p>
            </div>

            <OrganicShape className="p-6 bg-gradient-subtle">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary mb-2">
                  {Math.round(
                    (Object.values(answers).length /
                      assessmentQuestions.length) *
                      100
                  )}
                  %
                </div>
                <p className="text-sm text-foreground/80">Completion Rate</p>
              </div>
            </OrganicShape>

            <div className="flex justify-center">
              <Button
                onClick={async () => {
                  const results = await calculateResults();
                  onComplete?.(results);
                  onClose?.();
                }}
                className="hover:scale-105 transition-transform"
              >
                View Detailed Results
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const question = assessmentQuestions[currentQuestion];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Board Chemistry Assessment
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Progress Header */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Badge variant="muted" className="px-3 py-1">
                Question {currentQuestion + 1} of {assessmentQuestions.length}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSave}
                className={`hover:scale-105 transition-all ${
                  savedProgress ? "text-primary" : ""
                }`}
              >
                <Save className="w-4 h-4 mr-1" />
                {savedProgress ? "Saved!" : "Save Progress"}
              </Button>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Question Card */}
          <OrganicShape className="p-6 bg-gradient-subtle">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">
                  {question.title}
                </h3>
                <p className="text-foreground/80 leading-relaxed">
                  {question.scenario}
                </p>
              </div>

              {/* Answer Options */}
              <div className="space-y-3">
                {question.options.map((option) => {
                  const isSelected = answers[question.id] === option.id;
                  return (
                    <div
                      key={option.id}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                        isSelected
                          ? "border-primary bg-primary/10 shadow-md"
                          : "border-border hover:border-primary/50 hover:bg-primary/5"
                      }`}
                      onClick={() => handleAnswer(question.id, option.id)}
                    >
                      <div className="flex items-start gap-3">
                        {isSelected ? (
                          <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-foreground/40 mt-0.5 flex-shrink-0" />
                        )}
                        <span
                          className={`text-sm ${
                            isSelected
                              ? "text-foreground font-medium"
                              : "text-foreground/80"
                          }`}
                        >
                          {option.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </OrganicShape>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className="hover:scale-105 transition-transform"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>

            <Button
              onClick={handleNext}
              disabled={!canProceed}
              className="hover:scale-105 transition-transform"
            >
              {isLastQuestion ? "Complete Assessment" : "Next"}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
